import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Plus, Trash2, ArrowUp, ArrowDown, ArchiveRestore, Archive,
  Save, Eye, X, Loader2, GripVertical, FilePlus2,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Checkbox } from '../ui/checkbox';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../ui/alert-dialog';
import ChecklistRenderer, { flattenItems, initialValuesFor, EMPLOYEE_LABEL_HINTS, ASSET_LABEL_HINTS } from '../ChecklistRenderer';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Vocabulary must match backend seed + ChecklistRenderer supported types.
const ITEM_TYPES = [
  { value: 'yesno', label: 'Yes / No' },
  { value: 'yesno_na', label: 'Yes / No / N/A' },
  { value: 'check', label: 'Checkbox' },
  { value: 'text', label: 'Short text' },
  { value: 'note', label: 'Long text (note)' },
  { value: 'date', label: 'Date' },
  { value: 'time', label: 'Time' },
  { value: 'numeric', label: 'Numeric' },
  { value: 'select', label: 'Dropdown (single)' },
  { value: 'multiselect', label: 'Dropdown (multi)' },
  { value: 'signature', label: 'Signature' },
];

const TYPE_LABEL_BY_VALUE = Object.fromEntries(ITEM_TYPES.map((t) => [t.value, t.label]));

const NEW_ITEM = () => ({ type: 'yesno', label: '', required: false });
const NEW_SECTION = () => ({ name: 'New Section', items: [NEW_ITEM()] });

function reorder(arr, from, to) {
  if (to < 0 || to >= arr.length) return arr;
  const next = [...arr];
  const [it] = next.splice(from, 1);
  next.splice(to, 0, it);
  return next;
}

// A `select`/`multiselect` item whose label names a person or a piece of
// equipment is intentionally left without static options — it's filled
// live at runtime from the Team / Equipment list (see
// ChecklistRenderer.jsx's resolveSelectOptions). Mirrors that exact
// label-matching so the editor doesn't wrongly demand static options for
// e.g. "Employee Checking Equipment In:".
const hasDynamicOptionsSource = (label) => {
  const l = (label || '').toLowerCase();
  return EMPLOYEE_LABEL_HINTS.some((h) => l.includes(h)) || ASSET_LABEL_HINTS.some((h) => l.includes(h));
};

export default function ChecklistTemplateEditor() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showArchived, setShowArchived] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [draft, setDraft] = useState(null); // {title, subtitle, sections}
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [confirmArchive, setConfirmArchive] = useState(null);
  const [confirmDiscard, setConfirmDiscard] = useState(null);
  const [search, setSearch] = useState('');

  const fetchTemplates = async () => {
    setLoading(true);
    try {
      const resp = await axios.get(`${API}/checklist-templates`, {
        params: { include_archived: true },
      });
      setTemplates(Array.isArray(resp.data) ? resp.data : []);
    } catch (e) {
      toast.error('Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchTemplates(); }, []);

  const selectedTemplate = useMemo(
    () => templates.find((t) => t.id === selectedId) || null,
    [templates, selectedId],
  );

  const filteredTemplates = useMemo(() => {
    const q = search.trim().toLowerCase();
    return templates
      .filter((t) => showArchived ? true : !t.archived)
      .filter((t) => q ? (t.title || '').toLowerCase().includes(q) : true);
  }, [templates, showArchived, search]);

  const loadIntoDraft = (t) => {
    setDraft({
      title: t?.title || '',
      subtitle: t?.subtitle || '',
      sections: JSON.parse(JSON.stringify(t?.sections || [])),
    });
    setDirty(false);
  };

  const handleSelect = (id) => {
    if (dirty) { setConfirmDiscard(id); return; }
    setSelectedId(id);
    const t = templates.find((x) => x.id === id);
    if (t) loadIntoDraft(t);
    else setDraft(null);
  };

  const handleNewTemplate = () => {
    if (dirty) { setConfirmDiscard('__new__'); return; }
    setSelectedId(null);
    setDraft({ title: '', subtitle: '', sections: [NEW_SECTION()] });
    setDirty(true);
  };

  const patchDraft = (partial) => {
    setDraft((d) => ({ ...d, ...partial }));
    setDirty(true);
  };

  const patchSection = (sIdx, partial) => {
    setDraft((d) => {
      const sections = [...d.sections];
      sections[sIdx] = { ...sections[sIdx], ...partial };
      return { ...d, sections };
    });
    setDirty(true);
  };

  const patchItem = (sIdx, iIdx, partial) => {
    setDraft((d) => {
      const sections = [...d.sections];
      const items = [...sections[sIdx].items];
      items[iIdx] = { ...items[iIdx], ...partial };
      sections[sIdx] = { ...sections[sIdx], items };
      return { ...d, sections };
    });
    setDirty(true);
  };

  const addSection = () => {
    setDraft((d) => ({ ...d, sections: [...d.sections, NEW_SECTION()] }));
    setDirty(true);
  };

  const removeSection = (sIdx) => {
    setDraft((d) => ({ ...d, sections: d.sections.filter((_, i) => i !== sIdx) }));
    setDirty(true);
  };

  const moveSection = (sIdx, delta) => {
    setDraft((d) => ({ ...d, sections: reorder(d.sections, sIdx, sIdx + delta) }));
    setDirty(true);
  };

  const addItem = (sIdx) => {
    setDraft((d) => {
      const sections = [...d.sections];
      sections[sIdx] = { ...sections[sIdx], items: [...sections[sIdx].items, NEW_ITEM()] };
      return { ...d, sections };
    });
    setDirty(true);
  };

  const removeItem = (sIdx, iIdx) => {
    setDraft((d) => {
      const sections = [...d.sections];
      sections[sIdx] = {
        ...sections[sIdx],
        items: sections[sIdx].items.filter((_, i) => i !== iIdx),
      };
      return { ...d, sections };
    });
    setDirty(true);
  };

  const moveItem = (sIdx, iIdx, delta) => {
    setDraft((d) => {
      const sections = [...d.sections];
      sections[sIdx] = {
        ...sections[sIdx],
        items: reorder(sections[sIdx].items, iIdx, iIdx + delta),
      };
      return { ...d, sections };
    });
    setDirty(true);
  };

  const validate = () => {
    if (!draft) return 'No template to save';
    if (!draft.title?.trim()) return 'Title is required';
    for (let s = 0; s < draft.sections.length; s += 1) {
      const sec = draft.sections[s];
      if (!sec.name?.trim()) return `Section #${s + 1} needs a name`;
      for (let i = 0; i < sec.items.length; i += 1) {
        const it = sec.items[i];
        if (!it.label?.trim()) return `Section "${sec.name}" item #${i + 1} needs a label`;
        if ((it.type === 'select' || it.type === 'multiselect')
            && (!it.options || !it.options.length)
            && !hasDynamicOptionsSource(it.label)) {
          return `Section "${sec.name}" item "${it.label}" needs at least one option`;
        }
      }
    }
    return null;
  };

  const handleSave = async () => {
    const err = validate();
    if (err) { toast.error(err); return; }
    setSaving(true);
    try {
      const body = {
        title: draft.title.trim(),
        subtitle: (draft.subtitle || '').trim() || null,
        sections: draft.sections.map((s) => ({
          name: s.name.trim(),
          items: s.items.map((it) => ({
            type: it.type,
            label: it.label.trim(),
            required: !!it.required,
            ...(it.unit ? { unit: it.unit } : {}),
            ...(it.photo ? { photo: true } : {}),
            ...((it.type === 'select' || it.type === 'multiselect')
              ? { options: (it.options || []).map((o) => o.trim()).filter(Boolean) }
              : {}),
          })),
        })),
      };
      let resp;
      if (selectedId) {
        resp = await axios.put(`${API}/checklist-templates/${selectedId}`, body);
      } else {
        resp = await axios.post(`${API}/checklist-templates`, body);
      }
      toast.success(selectedId ? 'Template updated' : 'Template created');
      await fetchTemplates();
      setSelectedId(resp.data?.id);
      loadIntoDraft(resp.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleArchive = async () => {
    if (!selectedId) return;
    try {
      await axios.delete(`${API}/checklist-templates/${selectedId}`);
      toast.success('Template archived');
      await fetchTemplates();
      setConfirmArchive(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Archive failed');
    }
  };

  const handleRestore = async () => {
    if (!selectedId) return;
    try {
      const resp = await axios.post(`${API}/checklist-templates/${selectedId}/restore`);
      toast.success('Template restored');
      await fetchTemplates();
      loadIntoDraft(resp.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Restore failed');
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-6">
      {/* Left — template list */}
      <div className="bg-white rounded-lg border border-slate-200 p-4 h-fit lg:sticky lg:top-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-slate-900">Templates</h3>
          <Button
            size="sm"
            onClick={handleNewTemplate}
            className="bg-amber-500 hover:bg-amber-400 text-slate-900"
            data-testid="new-template-btn"
          >
            <FilePlus2 className="w-4 h-4 mr-1" />
            New
          </Button>
        </div>
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search templates..."
          className="mb-2"
          data-testid="template-search-input"
        />
        <label className="flex items-center gap-2 text-xs text-slate-600 mb-3">
          <Checkbox
            checked={showArchived}
            onCheckedChange={setShowArchived}
            data-testid="show-archived-toggle"
          />
          Show archived
        </label>
        {loading ? (
          <div className="flex items-center gap-2 text-sm text-slate-500 py-4">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading...
          </div>
        ) : (
          <div className="space-y-1 max-h-[60vh] overflow-y-auto">
            {filteredTemplates.length === 0 && (
              <p className="text-sm text-slate-500 py-4 text-center">No templates found.</p>
            )}
            {filteredTemplates.map((t) => (
              <button
                key={t.id}
                onClick={() => handleSelect(t.id)}
                className={`w-full text-left px-3 py-2 rounded text-sm transition-colors ${
                  selectedId === t.id
                    ? 'bg-amber-50 border border-amber-300'
                    : 'hover:bg-slate-50 border border-transparent'
                }`}
                data-testid={`template-row-${t.id}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="font-medium text-slate-900 truncate">{t.title}</span>
                  {t.archived && (
                    <span className="text-[10px] uppercase font-bold text-slate-400">
                      Archived
                    </span>
                  )}
                </div>
                <div className="text-xs text-slate-500">
                  {(t.sections || []).length} section(s)
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Right — editor */}
      <div>
        {!draft ? (
          <div className="bg-white rounded-lg border border-dashed border-slate-300 p-10 text-center">
            <p className="text-slate-500">
              Select a template on the left, or click <strong>New</strong> to create one.
            </p>
            <p className="text-xs text-slate-400 mt-2">
              Historical submissions are frozen — editing a template only affects future
              check-in / check-out / pre-trip submissions.
            </p>
          </div>
        ) : (
          <div className="bg-white rounded-lg border border-slate-200 p-4 space-y-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-end gap-3 justify-between">
              <div className="flex-1 space-y-3">
                <div>
                  <Label>Title <span className="text-red-500">*</span></Label>
                  <Input
                    value={draft.title}
                    onChange={(e) => patchDraft({ title: e.target.value })}
                    placeholder="Template title"
                    data-testid="template-title-input"
                  />
                </div>
                <div>
                  <Label>Subtitle (optional)</Label>
                  <Input
                    value={draft.subtitle || ''}
                    onChange={(e) => patchDraft({ subtitle: e.target.value })}
                    placeholder="Shown under the title"
                    data-testid="template-subtitle-input"
                  />
                </div>
              </div>
              <div className="flex flex-wrap gap-2 shrink-0">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowPreview(true)}
                  data-testid="preview-template-btn"
                >
                  <Eye className="w-4 h-4 mr-1" /> Preview
                </Button>
                {selectedTemplate?.archived ? (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleRestore}
                    className="border-emerald-300 text-emerald-700"
                    data-testid="restore-template-btn"
                  >
                    <ArchiveRestore className="w-4 h-4 mr-1" /> Restore
                  </Button>
                ) : selectedId ? (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setConfirmArchive(selectedId)}
                    className="border-rose-300 text-rose-700"
                    data-testid="archive-template-btn"
                  >
                    <Archive className="w-4 h-4 mr-1" /> Archive
                  </Button>
                ) : null}
                <Button
                  onClick={handleSave}
                  disabled={saving || !dirty}
                  className="bg-amber-500 hover:bg-amber-400 text-slate-900"
                  data-testid="save-template-btn"
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
                  Save
                </Button>
              </div>
            </div>

            {/* Sections */}
            <div className="space-y-4">
              {draft.sections.map((sec, sIdx) => (
                <div
                  key={sIdx}
                  className="border border-slate-200 rounded-lg p-3 bg-slate-50/50"
                  data-testid={`section-card-${sIdx}`}
                >
                  <div className="flex items-center gap-2 mb-3">
                    <GripVertical className="w-4 h-4 text-slate-400 shrink-0" />
                    <Input
                      value={sec.name}
                      onChange={(e) => patchSection(sIdx, { name: e.target.value })}
                      placeholder="Section name"
                      className="font-semibold"
                      data-testid={`section-name-${sIdx}`}
                    />
                    <Button variant="ghost" size="icon" onClick={() => moveSection(sIdx, -1)} disabled={sIdx === 0}>
                      <ArrowUp className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => moveSection(sIdx, 1)} disabled={sIdx === draft.sections.length - 1}>
                      <ArrowDown className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => removeSection(sIdx)}
                      className="text-rose-600 hover:text-rose-700"
                      data-testid={`remove-section-${sIdx}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>

                  {/* Items */}
                  <div className="space-y-2">
                    {sec.items.map((it, iIdx) => (
                      <ItemRow
                        key={iIdx}
                        item={it}
                        sIdx={sIdx}
                        iIdx={iIdx}
                        onChange={(partial) => patchItem(sIdx, iIdx, partial)}
                        onRemove={() => removeItem(sIdx, iIdx)}
                        onMoveUp={() => moveItem(sIdx, iIdx, -1)}
                        onMoveDown={() => moveItem(sIdx, iIdx, 1)}
                        firstItem={iIdx === 0}
                        lastItem={iIdx === sec.items.length - 1}
                      />
                    ))}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => addItem(sIdx)}
                      className="w-full border-dashed"
                      data-testid={`add-item-btn-${sIdx}`}
                    >
                      <Plus className="w-4 h-4 mr-1" /> Add item
                    </Button>
                  </div>
                </div>
              ))}
              <Button
                variant="outline"
                onClick={addSection}
                className="w-full border-dashed"
                data-testid="add-section-btn"
              >
                <Plus className="w-4 h-4 mr-1" /> Add section
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Preview */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Preview: {draft?.title || 'Untitled'}</DialogTitle>
          </DialogHeader>
          <TemplatePreview sections={draft?.sections || []} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPreview(false)}>
              <X className="w-4 h-4 mr-1" /> Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Archive confirmation */}
      <AlertDialog open={!!confirmArchive} onOpenChange={(o) => !o && setConfirmArchive(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Archive this template?</AlertDialogTitle>
            <AlertDialogDescription>
              This soft-deletes the template. It&apos;s hidden from equipment settings and any
              new picker, but stays in the database so historical submissions still resolve.
              You can restore it later via <strong>Show archived</strong>.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleArchive}
              className="bg-rose-600 hover:bg-rose-500 text-white"
              data-testid="confirm-archive-btn"
            >
              Archive
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Discard-unsaved confirmation */}
      <AlertDialog open={!!confirmDiscard} onOpenChange={(o) => !o && setConfirmDiscard(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Discard unsaved changes?</AlertDialogTitle>
            <AlertDialogDescription>
              You have unsaved changes on the current template. Switching now will lose them.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep editing</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                const target = confirmDiscard;
                setConfirmDiscard(null);
                if (target === '__new__') {
                  setSelectedId(null);
                  setDraft({ title: '', subtitle: '', sections: [NEW_SECTION()] });
                  setDirty(true);
                } else {
                  setSelectedId(target);
                  const t = templates.find((x) => x.id === target);
                  if (t) loadIntoDraft(t);
                }
              }}
              className="bg-rose-600 hover:bg-rose-500 text-white"
            >
              Discard
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function ItemRow({ item, sIdx, iIdx, onChange, onRemove, onMoveUp, onMoveDown, firstItem, lastItem }) {
  const needsOptions = item.type === 'select' || item.type === 'multiselect';
  const canHaveUnit = item.type === 'numeric';
  const canHavePhoto = ['yesno', 'yesno_na', 'check', 'text', 'note'].includes(item.type);

  return (
    <div className="border border-slate-200 rounded p-3 bg-white space-y-2" data-testid={`item-row-${sIdx}-${iIdx}`}>
      <div className="grid grid-cols-1 sm:grid-cols-[1fr_170px_auto] gap-2">
        <Input
          value={item.label}
          onChange={(e) => onChange({ label: e.target.value })}
          placeholder="Item label"
          data-testid={`item-label-${sIdx}-${iIdx}`}
        />
        <Select value={item.type} onValueChange={(v) => onChange({ type: v })}>
          <SelectTrigger data-testid={`item-type-${sIdx}-${iIdx}`}>
            <SelectValue>{TYPE_LABEL_BY_VALUE[item.type] || item.type}</SelectValue>
          </SelectTrigger>
          <SelectContent>
            {ITEM_TYPES.map((t) => (
              <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="icon" onClick={onMoveUp} disabled={firstItem}>
            <ArrowUp className="w-4 h-4" />
          </Button>
          <Button variant="ghost" size="icon" onClick={onMoveDown} disabled={lastItem}>
            <ArrowDown className="w-4 h-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={onRemove}
            className="text-rose-600"
            data-testid={`remove-item-${sIdx}-${iIdx}`}
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-4 text-xs text-slate-600">
        <label className="flex items-center gap-1.5">
          <Checkbox
            checked={!!item.required}
            onCheckedChange={(v) => onChange({ required: !!v })}
            data-testid={`item-required-${sIdx}-${iIdx}`}
          />
          Required
        </label>
        {canHavePhoto && (
          <label className="flex items-center gap-1.5">
            <Checkbox
              checked={!!item.photo}
              onCheckedChange={(v) => onChange({ photo: !!v })}
            />
            Allow photo attachment
          </label>
        )}
        {canHaveUnit && (
          <div className="flex items-center gap-1.5">
            <span>Unit:</span>
            <Input
              value={item.unit || ''}
              onChange={(e) => onChange({ unit: e.target.value })}
              placeholder="e.g. psi, in, %"
              className="h-7 w-24 text-xs"
            />
          </div>
        )}
      </div>
      {needsOptions && (
        <div>
          <Label className="text-xs">Options (one per line)</Label>
          {hasDynamicOptionsSource(item.label) ? (
            <p className="text-xs text-slate-500 italic mb-1" data-testid={`item-options-dynamic-hint-${sIdx}-${iIdx}`}>
              Leave blank — this field auto-fills from the live Team / Equipment list when the form is
              filled out, based on its label.
            </p>
          ) : null}
          <Textarea
            value={(item.options || []).join('\n')}
            onChange={(e) => onChange({ options: e.target.value.split('\n') })}
            placeholder={'Option A\nOption B\nOption C'}
            rows={Math.max(3, (item.options || []).length)}
            data-testid={`item-options-${sIdx}-${iIdx}`}
          />
        </div>
      )}
    </div>
  );
}

/**
 * Read-only preview using the same ChecklistRenderer so admins see the
 * exact runtime rendering. Uses initialValuesFor + a no-op setValues so
 * the fields render but changes don't affect the editor state.
 */
function TemplatePreview({ sections }) {
  const flat = useMemo(() => flattenItems(sections), [sections]);
  const [values, setValues] = useState(() => initialValuesFor(flat));
  useEffect(() => { setValues(initialValuesFor(flat)); }, [flat]);
  const handleChange = (idx, patch) => {
    setValues((prev) => ({ ...prev, [idx]: { ...(prev[idx] || {}), ...patch } }));
  };
  return (
    <div className="rounded border border-slate-200 p-4 bg-slate-50">
      <ChecklistRenderer
        flatItems={flat}
        values={values}
        onChange={handleChange}
        readOnly
        testIdPrefix="tpl-preview"
        // Photo button visibility only depends on item.photo === true and
        // onPhotoUpload being present — pass a no-op so admins can SEE the
        // camera icon show up here exactly like it will on the real form
        // (there's nowhere real to upload to in a preview, so it stays
        // disabled with its own explanatory tooltip instead of doing
        // nothing silently).
        onPhotoUpload={() => {}}
        photosDisabled
        photoDisabledTooltip="Preview only — photo upload works on the real form, not testable here"
        photoDisabledLabel="Preview only"
      />
    </div>
  );
}
